// frontend/src/pages/Suppliers/Suppliers.jsx
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  Building,
  CreditCard,
  Loader2,
  Mail,
  Phone,
  Save,
  User,
  UserPlus,
  X,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./Suppliers.css";

const url = process.env.REACT_APP_BACKEND_URL;

// ✅ ROBUST: ALWAYS returns a 3‑letter currency code as a string
const getCompanyCurrency = (company) => {
  if (!company) return "USD";

  let currency = null;

  // 1. Try company.currency (could be object or string)
  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }

  // 2. Try company.currency_code
  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }

  // 3. Try company.base_currency
  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }

  // Final safety: default to USD if not a valid 3-letter code
  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }

  return currency;
};

const initialForm = (companyId = "", companyCurrency = "USD") => ({
  supplier_name: "",
  phone: "",
  email: "",
  currency_code: companyCurrency,
  credit_limit: "0.00",
  company_id: companyId,
});

export default function Suppliers() {
  const { currentUser, userType, company } = useAuth();
  const { getCurrencySymbol } = useCurrency();
  const navigate = useNavigate();

  const companyId = company?._id || company || currentUser?.company_id || "";
  const isSystemAdmin =
    userType === "system" && ["developer", "admin"].includes(currentUser?.role);
  const isCompanyUser =
    userType === "company" &&
    ["admin", "purchase", "user"].includes(currentUser?.role);
  const canCreate = isSystemAdmin || isCompanyUser;

  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  const [formData, setFormData] = useState(() =>
    initialForm(isSystemAdmin ? "" : companyId, companyCurrency)
  );
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const selectedCompanyName = useMemo(() => {
    if (!isSystemAdmin) return company?.company_name || "your company";
    return (
      companies.find((item) => item._id === formData.company_id)?.company_name ||
      "the selected company"
    );
  }, [companies, company?.company_name, formData.company_id, isSystemAdmin]);

  useEffect(() => {
    if (isSystemAdmin && formData.company_id) {
      const selectedCompany = companies.find((c) => c._id === formData.company_id);
      if (selectedCompany) {
        const currency = getCompanyCurrency(selectedCompany);
        setFormData((prev) => ({
          ...prev,
          currency_code: currency,
        }));
      }
    }
  }, [formData.company_id, companies, isSystemAdmin]);

  useEffect(() => {
    let active = true;

    const setup = async () => {
      if (!canCreate) {
        toast.error("You do not have permission to create suppliers");
        navigate("/");
        return;
      }

      if (isSystemAdmin) {
        try {
          const response = await axios.get(`${url}/api/company`);
          if (active) setCompanies(response.data?.data || []);
        } catch (error) {
          toast.error(error.response?.data?.message || "Failed to load companies");
        }
      } else if (active) {
        setFormData((current) => ({
          ...current,
          company_id: companyId,
          currency_code: companyCurrency,
        }));
      }

      if (active) setLoading(false);
    };

    setup();
    return () => {
      active = false;
    };
  }, [canCreate, companyId, companyCurrency, isSystemAdmin, navigate]);

  const setField = (name, value) => {
    setFormData((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    const name = formData.supplier_name.trim();
    const creditLimit = Number(formData.credit_limit || 0);

    if (name.length < 2 || name.length > 100) {
      nextErrors.supplier_name = "Supplier name must contain 2 to 100 characters";
    }
    if (formData.phone && !/^[\d\s+()\-]{7,30}$/.test(formData.phone.trim())) {
      nextErrors.phone = "Enter a valid phone number";
    }
    if (
      formData.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())
    ) {
      nextErrors.email = "Enter a valid email address";
    }
    if (!/^[A-Za-z]{3}$/.test(formData.currency_code)) {
      nextErrors.currency_code = "Use a three-letter currency code, such as USD";
    }
    if (!Number.isFinite(creditLimit) || creditLimit < 0) {
      nextErrors.credit_limit = "Credit limit must be zero or greater";
    }
    if (!formData.company_id) {
      nextErrors.company_id = "Company selection is required";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) {
      toast.error("Please correct the highlighted fields");
      return;
    }

    const payload = {
      supplier_name: formData.supplier_name.trim(),
      phone: formData.phone.trim(),
      email: formData.email.trim().toLowerCase(),
      currency_code: formData.currency_code.trim().toUpperCase(),
      credit_limit: Number(formData.credit_limit || 0),
      company_id: formData.company_id,
    };

    try {
      setSubmitting(true);
      const response = await axios.post(`${url}/api/supplier`, payload);

      toast.success(response.data?.message || "Supplier created successfully");
      setFormData(initialForm(isSystemAdmin ? "" : companyId, companyCurrency));
      window.setTimeout(() => navigate("/ViewSuppliers"), 500);
    } catch (error) {
      const message = error.response?.data?.message || "Failed to create supplier";
      toast.error(message);

      const serverErrors = error.response?.data?.errors;
      if (Array.isArray(serverErrors)) {
        setErrors((current) => ({
          ...current,
          ...Object.fromEntries(serverErrors.map((item) => [item.field, item.message])),
        }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="supplier-page-shell">
        <div className="supplier-loading-card">
          <Loader2 className="supplier-spin" size={30} />
          <span>Loading supplier form…</span>
        </div>
      </main>
    );
  }

  return (
    <main className="supplier-page-shell">
      <section className="supplier-form-card">
        <button
          type="button"
          className="supplier-close-button"
          onClick={() => navigate("/ViewSuppliers")}
          aria-label="Close supplier form"
        >
          <X size={20} />
        </button>

        <header className="supplier-form-header">
          <div className="supplier-form-icon"><UserPlus size={24} /></div>
          <div>
            <p className="supplier-eyebrow">Supplier master</p>
            <h1>Add supplier</h1>
            <p>Create a supplier record for {selectedCompanyName}.</p>
          </div>
        </header>

        <div className="supplier-accounting-note">
          <AlertCircle size={20} />
          <div>
            <strong>Supplier creation does not post the Chart of Accounts.</strong>
            <span>
              Opening balances must be entered through the Beginning Balance Entry screen.
              This form only creates master data.
            </span>
          </div>
        </div>

        <form className="supplier-form" onSubmit={handleSubmit} noValidate>
          {isSystemAdmin && (
            <div className="supplier-field supplier-field--full">
              <label htmlFor="supplier-company"><Building size={15} /> Company *</label>
              <select
                id="supplier-company"
                value={formData.company_id}
                onChange={(event) => setField("company_id", event.target.value)}
              >
                <option value="">Select company</option>
                {companies.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.company_name}{item.company_code ? ` (${item.company_code})` : ""}
                  </option>
                ))}
              </select>
              {errors.company_id && <small className="supplier-error">{errors.company_id}</small>}
            </div>
          )}

          <div className="supplier-field supplier-field--full">
            <label htmlFor="supplier-name"><User size={15} /> Supplier name *</label>
            <input
              id="supplier-name"
              value={formData.supplier_name}
              onChange={(event) => setField("supplier_name", event.target.value)}
              placeholder="Example: Hodan Wholesale Supplier"
              maxLength={100}
            />
            {errors.supplier_name && <small className="supplier-error">{errors.supplier_name}</small>}
          </div>

          <div className="supplier-field">
            <label htmlFor="supplier-phone"><Phone size={15} /> Phone</label>
            <input
              id="supplier-phone"
              value={formData.phone}
              onChange={(event) => setField("phone", event.target.value)}
              placeholder="Optional"
              maxLength={30}
            />
            {errors.phone && <small className="supplier-error">{errors.phone}</small>}
          </div>

          <div className="supplier-field">
            <label htmlFor="supplier-email"><Mail size={15} /> Email</label>
            <input
              id="supplier-email"
              type="email"
              value={formData.email}
              onChange={(event) => setField("email", event.target.value)}
              placeholder="Optional"
              maxLength={120}
            />
            {errors.email && <small className="supplier-error">{errors.email}</small>}
          </div>

          <div className="supplier-field">
            <label htmlFor="supplier-currency"><CreditCard size={15} /> Currency</label>
            <input
              id="supplier-currency"
              value={formData.currency_code}
              disabled
              style={{
                background: '#f5f5f5',
                cursor: 'not-allowed',
                opacity: 0.8,
              }}
            />
            <small style={{ color: '#666' }}>
              Currency is set to the company's base currency and cannot be changed.
            </small>
            {errors.currency_code && <small className="supplier-error">{errors.currency_code}</small>}
          </div>

          <div className="supplier-field">
            <label htmlFor="supplier-credit-limit"><CreditCard size={15} /> Credit limit</label>
            <input
              id="supplier-credit-limit"
              type="number"
              min="0"
              step="0.01"
              value={formData.credit_limit}
              onChange={(event) => setField("credit_limit", event.target.value)}
            />
            <small className="supplier-hint">
              Operational control only; zero means no limit is configured. It does not create an accounting entry.
            </small>
            {errors.credit_limit && <small className="supplier-error">{errors.credit_limit}</small>}
          </div>

          <div className="supplier-form-actions supplier-field--full">
            <button
              type="button"
              className="supplier-button supplier-button--secondary"
              onClick={() => navigate("/ViewSuppliers")}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="supplier-button supplier-button--primary"
              disabled={submitting}
            >
              {submitting ? <Loader2 className="supplier-spin" size={17} /> : <Save size={17} />}
              {submitting ? "Creating…" : "Create supplier"}
            </button>
          </div>
        </form>
      </section>
    </main>
  );
}